import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Profile } from "@/integrations/supabase/types/subscription";

export function useSettingsHandlers() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubscribe = async (priceId: string) => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const response = await supabase.functions.invoke('create-checkout-session', {
        body: { 
          priceId,
          userId: user.id,
        }
      });

      if (response.error) throw response.error;
      if (!response.data?.url) throw new Error('No checkout URL received');
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke starte abonnementsprosessen",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      navigate("/login");
      toast({
        title: "Logget ut",
        description: "Du har blitt logget ut av systemet.",
      });
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke logge ut. Prøv igjen senere.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Ingen bruker funnet");

      const { error } = await supabase
        .from("profiles")
        .update({
          ...updates,
          notification_preferences: updates.notification_preferences 
            ? { ...updates.notification_preferences }
            : undefined,
        })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: "Oppdatert",
        description: "Innstillingene dine har blitt oppdatert",
      });
    } catch (error) {
      console.error('Profile update error:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke oppdatere innstillinger",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleSubscribe,
    handleLogout,
    updateProfile
  };
}